import { COLORS } from "@coeff/utils";
import { Tooltip } from "antd";
import React, { HTMLAttributes, MouseEventHandler, PropsWithChildren, useState } from "react";
import styled, { DefaultTheme } from "styled-components";

export type TypographyProps<T = Element> = HTMLAttributes<T> & {
  title?: string;
  variant?: string;
  lineHeight?: number | string;
  gutterBottom?: boolean | number;
  align?: "center" | "unset" | "left" | "right";
  color?: "textPrimary" | "textSecondary" | "textTertiary" | "textLink" | "error";
  fontWeight?: "normal" | "bold" | number;
  noWrap?: boolean;
  hasTooltip?: boolean;
  tooltipDelay?: number;
  underline?: boolean;
  inline?: boolean;
  lineClamp?: number;
  onMouseEnter?: MouseEventHandler;
  hoverable?: boolean;
  rtl?: boolean;
  ellipsis?: boolean;
  uppercase?: boolean;
  fontSize?: number;
};

export const StyledTypography = styled.div<TypographyProps>`
  color: ${({ variant, color, theme }) => {
    const colors = theme?.colors || COLORS;
    if (color !== undefined) {
      switch (color) {
        case "textPrimary":
          return colors.black85;
        case "textSecondary":
          return colors.black45;
        case "textTertiary":
          return colors.black35;
        case "textLink":
          return colors.coeblue4;
        case "error":
          return colors.red;
        default:
          return colors.black85;
      }
    }

    switch (variant) {
      case "caption":
        return colors.black25;
      default:
        return colors.black85;
    }
  }};

  ${({ theme, variant }) => {
    if (variant === undefined) {
      return "";
    }

    const typography = theme?.typography;
    if (!typography) {
      return "";
    }

    return `font-size: ${typography[variant].fontSize}px`;
  }}}

  ${({ inline }) => {
    if (inline === undefined) {
      return "";
    }

    return `
      display: inline;
    `;
  }}

  ${({ fontWeight }) => {
    if (fontWeight === undefined) {
      return "";
    }

    return `
      font-weight: ${fontWeight};
    `;
  }}

  ${({ underline }) => {
    if (underline === undefined) {
      return "";
    }

    return "text-decoration: underline dotted;";
  }}


  ${({ lineHeight }) => {
    return lineHeight ? `line-height: ${lineHeight};` : "";
  }}

  ${({ gutterBottom, theme }) => {
    if (gutterBottom === undefined || !gutterBottom) {
      return "";
    }

    const spacing = theme?.spacing || ((unit: number) => `${unit * 8}px`);
    return `
      margin-bottom: ${spacing(typeof gutterBottom === "number" ? gutterBottom : 1)};
    `;
  }}

  ${({ align }) => {
    if (align === undefined) {
      return "";
    }

    return `
      text-align: ${align};
    `;
  }}

  ${({ uppercase }) => {
    if (uppercase === undefined) {
      return "";
    }

    return `
      text-transform: uppercase;
    `;
  }}

  ${({ ellipsis }) => {
    if (ellipsis) {
      return `
        text-overflow: ellipsis;
        overflow: hidden;
      `;
    } else {
      return "";
    }
  }}

  ${({ noWrap, lineClamp }) => {
    if (noWrap || lineClamp !== undefined) {
      return `
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      `;
    } else {
      return "";
    }
  }}

  ${({ lineClamp }) => {
    if (lineClamp !== undefined) {
      return `
          display: -webkit-inline-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${lineClamp};
          overflow: hidden;
          white-space: normal;
      `;
    } else {
      return "";
    }
  }}

  ${({ hoverable, theme }) => {
    if (hoverable) {
      const colors = theme?.colors || COLORS;
      return `
          &:hover {
            color: ${colors.coeblue4}
          }
      `;
    } else {
      return "";
    }
  }}

  ${({ rtl }) => {
    if (rtl) {
      return `
        direction: rtl;
        text-align: left;
      `;
    } else {
      return "";
    }
  }}

  ${({ fontSize }) => {
    if (fontSize !== undefined) {
      return `
          font-size: ${fontSize}px;
      `;
    } else {
      return "";
    }
  }}
`;

export const Typography: React.FC<PropsWithChildren<TypographyProps>> = props => {
  const { children, hasTooltip, title, tooltipDelay, ...rest } = props;

  const [tooltipDisabled, setTooltipDisabled] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const { offsetWidth, scrollWidth } = e.target as HTMLDivElement;

    // Only show the Tooltip if the text is ellipsed. If lineClamp
    // is applied, always show it since offsetWidth is always equal
    // to scrollWidth
    // eslint-disable-next-line @coeff/strict-boolean-expressions
    if (offsetWidth >= scrollWidth && !rest.lineClamp) {
      setTooltipDisabled(true);
    }
  };

  if (hasTooltip) {
    return (
      <Tooltip mouseEnterDelay={tooltipDelay ?? 1} title={tooltipDisabled ? "" : title ?? children}>
        <StyledTypography {...rest} onMouseEnter={handleMouseEnter} noWrap>
          {children}
        </StyledTypography>
      </Tooltip>
    );
  }

  return <StyledTypography {...rest}>{children}</StyledTypography>;
};
